<template>
  <app-timeline
    size="sm"
    :canceled-step="TransferStatus.CANCELED"
  >
    <app-timeline-item>
      <template #icon>
        <country-flag
          :country-code="sourceCountry"
          :is-loading="isLoading"
          class="size-6"
        />
      </template>
      <template #content>
        <div class="flex justify-between">
          <div>
            <p class="text-base font-medium">{{ transfer?.released_at ? 'Total sent' : 'Total due' }}</p>
            <div
              v-if="isLoading"
              class="skeleton h-5"
            ></div>
            <span
              class="text-gray-600"
              v-else-if="transfer?.released_at"
            >
              {{ formatDate(transfer?.released_at, 'll') }}
            </span>
          </div>
          <div
            v-if="isLoading"
            class="skeleton h-5 w-16"
          ></div>
          <p
            v-else
            class="text-base font-semibold"
          >
            {{ totalSent }}
          </p>
        </div>
      </template>
    </app-timeline-item>
    <app-timeline-item>
      <template #icon>
        <country-flag
          :country-code="targetCountry"
          :is-loading="isLoading"
          class="size-6"
        />
      </template>
      <template #content>
        <div class="flex justify-between">
          <div>
            <p class="text-base font-medium">Total {{ receivedTimelineItem.label }}</p>
            <div
              v-if="isLoading"
              class="skeleton h-5"
            ></div>
            <span
              v-else
              :class="{ 'opacity-33': isFailed }"
            >
              {{ receivedTimelineItem.statusValue }}
            </span>
          </div>
          <div class="text-right">
            <div
              v-if="isLoading"
              class="skeleton h-5 w-14 mb-1"
            ></div>
            <p
              v-else
              :class="['text-base', 'font-semibold', { 'opacity-33': isFailed }]"
            >
              {{ totalReceived }}
            </p>
            <div
              v-if="isLoading"
              class="skeleton h-5"
            ></div>
            <p
              v-else-if="(isSupplierRole && transfer?.settled_at) || !isSupplierRole"
              class="text-gray-600"
            >
              {{ fxQuote }}
            </p>
          </div>
        </div>
      </template>
    </app-timeline-item>
  </app-timeline>
</template>

<script setup>
import { computed } from 'vue';
import { CurrencyCountryCodes } from '@/constants/currencies';
import { formatNumber } from '@/utils/number';
import { formatDate } from '@/utils/dates';
import CountryFlag from '@/components/ui/CountryFlag.vue';
import AppTimeline from '@/components/ui/timeline/Timeline.vue';
import AppTimelineItem from '@/components/ui/timeline/TimelineItem.vue';
import { TransferStatus } from '@/constants/transfers.js';
import { useAuthStore } from '@/stores/auth';
import { SionUserTypes } from '@/constants/sion.js';

const props = defineProps({
  transfer: {
    type: Object,
    default: () => {},
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const authStore = useAuthStore();
const isSupplierRole = computed(() => authStore.userType === SionUserTypes.SUPPLIER);
const isFailed = computed(() => props.transfer?.status === TransferStatus.CANCELED);

const receivedTimelineItem = computed(() => {
  const { status, settled_at } = props.transfer || {};
  const label = getReceivedLabel(status);
  const statusValue = getReceivedStatusValue(status, settled_at);
  return { label, statusValue };
});

const getReceivedLabel = (status) => {
  if (isSupplierRole.value) {
    return 'received';
  }

  const labelMap = {
    [TransferStatus.CANCELED]: 'amount',
    [TransferStatus.RELEASED]: 'incoming',
    [TransferStatus.SETTLED]: 'received',
  };

  return labelMap[status];
};

const getReceivedStatusValue = (status, settledDate) => {
  if (isFailed.value) {
    return isSupplierRole.value ? 'Canceled' : 'Failed';
  }

  if (settledDate) {
    return formatDate(settledDate, 'll');
  }

  return 'Pending';
};

const totalSent = computed(() => {
  return formatNumber({
    value: props.transfer?.source_amount || 0,
    options: { style: 'currency', currency: props.transfer?.source_currency_alpha_code || 'USD' },
  });
});

const totalReceived = computed(() => {
  let value = 0;

  if (isSupplierRole.value) {
    value = props.transfer?.settled_at ? props.transfer?.target_amount : 0;
  } else {
    value = props.transfer?.target_amount;
  }

  return formatNumber({
    value: value,
    options: { style: 'currency', currency: props.transfer?.target_currency_alpha_code || 'USD' },
  });
});

const fxQuote = computed(() => {
  return `@${props.transfer?.fx_quote?.rate || 1} ${props.transfer?.source_currency_alpha_code}/${props.transfer.target_currency_alpha_code}`;
});

const sourceCountry = computed(() => {
  return CurrencyCountryCodes[props.transfer?.source_currency_alpha_code] || 'XX';
});

const targetCountry = computed(() => {
  return CurrencyCountryCodes[props.transfer?.target_currency_alpha_code] || 'XX';
});
</script>
